import { LockIcon } from 'lucide-react';

import { cn, Stack, TableCell } from '@crossnokaye/ux-common';

import {
  getCompressorSequenceState,
  trimBackground,
} from '@/features/equipment-browser/sequencer-section/compressor-sequencer/helpers';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipPortal,
  TooltipTrigger,
} from '@/ux-common/components/tooltip/tooltip';

import { CompressorEnergyAIRampSlider } from './compressor-energy-ai-ramp-slider';
import { ENERGY_AI_CELL_TOOLTIP_DETAIL } from '../compressor-energy-ai.constants';
import type { EnergyAICompressorState, EnergyAIStageRow } from '../compressor-energy-ai.types';
import { formatEnergyAIRampAriaLabel } from '../compressor-energy-ai.utils';

/**
 * Background class for a cell Energy AI is filling. Trim is absent on purpose -
 * it needs a gradient, which is applied as an inline style rather than a class.
 */
const getStageFillClassName = (state: EnergyAICompressorState) => {
  if (state === 'on') {
    return 'bg-ckds-brand-500';
  }
  if (state === 'unknown') {
    return 'bg-ckds-brand-500/25';
  }
  return undefined;
};

const getEnergyAIStageTooltipContent = (row: EnergyAIStageRow, isUnavailable: boolean) => {
  if (isUnavailable) {
    return 'Unavailable';
  }
  if (row.segment?.stageValue === undefined) {
    return 'Unknown';
  }
  return getCompressorSequenceState(row.segment.stageValue);
};

/**
 * Builds the text announced for a stage cell. The Energy AI column is a
 * separate single-column table with no row headers, so this string carries the
 * compressor name itself — without it the cell is unidentifiable to a screen
 * reader.
 */
const getEnergyAIStageCellDescription = ({
  isLockedByOther,
  isUnavailableInSequence,
  row,
}: {
  isLockedByOther: boolean;
  isUnavailableInSequence: boolean;
  row: EnergyAIStageRow;
}) => {
  const description = formatEnergyAIRampAriaLabel(row);
  if (isLockedByOther) {
    return `${description}, locked by another sequencer`;
  }
  if (isUnavailableInSequence && row.state !== 'unavailable') {
    return `${description}, unavailable in this sequence`;
  }
  return description;
};

interface CompressorEnergyAIStageCellProps {
  isEnergyAIActive: boolean;
  isLockedByOther: boolean;
  isUnavailableInSequence: boolean;
  row: EnergyAIStageRow;
}

export const CompressorEnergyAIStageCell = ({
  isEnergyAIActive,
  isLockedByOther,
  isUnavailableInSequence,
  row,
}: CompressorEnergyAIStageCellProps) => {
  const isUnavailable = isUnavailableInSequence || row.state === 'unavailable';
  const shouldSuppressAction = isLockedByOther || isUnavailable;
  const isRamping = row.progress !== undefined && row.direction !== 'none';
  const showEnergyAIFill =
    isEnergyAIActive &&
    !isRamping &&
    !isLockedByOther &&
    row.state !== 'off' &&
    row.state !== 'unavailable';
  const canFillCell = showEnergyAIFill && !isUnavailableInSequence;
  const cellBackgroundClassName = canFillCell ? getStageFillClassName(row.state) : undefined;
  const cellBackgroundStyle =
    canFillCell && row.state === 'trim' ? { background: trimBackground } : undefined;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <TableCell className="h-[inherit] border-b-1 border-l-1 border-black p-0">
          <div
            className={cn(
              'relative flex h-full items-center justify-center bg-ckds-neutral-900 text-xs font-semibold text-ckds-text-fg-default',
              shouldSuppressAction && 'text-ckds-text-fg-tier3',
              cellBackgroundClassName
            )}
            data-component="CompressorEnergyAIStageCell"
            style={cellBackgroundStyle}
          >
            <span className="sr-only">
              {getEnergyAIStageCellDescription({
                isLockedByOther,
                isUnavailableInSequence,
                row,
              })}
            </span>
            {shouldSuppressAction ? <LockIcon aria-hidden="true" className="h-4 w-4" /> : null}
            {shouldSuppressAction || !isEnergyAIActive ? null : (
              <CompressorEnergyAIRampSlider row={row} />
            )}
          </div>
        </TableCell>
      </TooltipTrigger>
      <TooltipPortal>
        <TooltipContent align="center" className="m-0 gap-0.5" side="bottom">
          <Stack gap="thin">
            <div className="flex items-center gap-1">
              <span className="font-bold">
                {getEnergyAIStageTooltipContent(row, isUnavailable)}
              </span>
            </div>
            <span>{ENERGY_AI_CELL_TOOLTIP_DETAIL}</span>
          </Stack>
          <TooltipArrow />
        </TooltipContent>
      </TooltipPortal>
    </Tooltip>
  );
};
